import { useEffect, useState } from 'react'

import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Select } from 'components/ui/select'
import { cn } from 'lib/utils'

import * as presentationService from '../services/presentationService'
import type { PresentationSummary } from '../types/presentation.types'

/**
 * Where something from Explorer goes (MYD-506): a new deck, named here, or
 * one of the user's own existing decks. Only their own: adding to a deck is
 * editing it, and a partner's shared deck is not theirs to edit.
 */
export type DeckTarget = { kind: 'new'; name: string } | { kind: 'existing'; id: string; name: string; slideCount: number }

interface DeckTargetFieldProps {
	open: boolean
	defaultName: string
	onChange: (target: DeckTarget | null) => void
}

export function DeckTargetField({ open, defaultName, onChange }: DeckTargetFieldProps) {
	const [kind, setKind] = useState<'new' | 'existing'>('new')
	const [name, setName] = useState(defaultName)
	const [decks, setDecks] = useState<PresentationSummary[] | null>(null)
	const [deckId, setDeckId] = useState('')

	useEffect(() => {
		if (!open) return
		setKind('new')
		setName(defaultName)
		setDeckId('')
		let cancelled = false
		presentationService
			.listPresentations()
			.then(found => !cancelled && setDecks(found.filter(deck => deck.isOwner)))
			.catch(() => !cancelled && setDecks([]))
		return () => {
			cancelled = true
		}
	}, [open, defaultName])

	useEffect(() => {
		if (kind === 'new') {
			onChange(name.trim() ? { kind: 'new', name: name.trim() } : null)
			return
		}
		const deck = decks?.find(candidate => candidate.id === deckId)
		onChange(deck ? { kind: 'existing', id: deck.id, name: deck.name, slideCount: deck.slideCount } : null)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [kind, name, deckId, decks])

	const choice = (value: 'new' | 'existing', label: string) => (
		<button
			type="button"
			role="radio"
			aria-checked={kind === value}
			onClick={() => setKind(value)}
			className={cn('rounded px-2 py-1.5 text-xs font-medium', kind === value ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-50')}
		>
			{label}
		</button>
	)

	return (
		<div className="space-y-2">
			<div role="radiogroup" aria-label="Where it goes" className="grid grid-cols-2 rounded-md border border-gray-200 p-0.5">
				{choice('new', 'New presentation')}
				{choice('existing', 'Existing presentation')}
			</div>
			{kind === 'new' ? (
				<div className="space-y-1.5">
					<Label htmlFor="deck-target-name">Name</Label>
					<Input id="deck-target-name" value={name} maxLength={140} onChange={event => setName(event.target.value)} />
				</div>
			) : decks !== null && decks.length === 0 ? (
				<p className="text-xs text-muted-foreground">You have no presentations of your own yet.</p>
			) : (
				<div className="space-y-1.5">
					<Label htmlFor="deck-target-existing">Presentation</Label>
					<Select id="deck-target-existing" value={deckId} onChange={event => setDeckId(event.target.value)}>
						<option value="">{decks === null ? 'Loading...' : 'Choose a presentation'}</option>
						{(decks ?? []).map(deck => (
							<option key={deck.id} value={deck.id}>
								{deck.name} ({deck.slideCount} {deck.slideCount === 1 ? 'slide' : 'slides'})
							</option>
						))}
					</Select>
				</div>
			)}
		</div>
	)
}

export default DeckTargetField
